import { useState } from "react";
import { useLang } from "@/lib/lang";
import { linkCaseDocument, unlinkCaseDocument, uploadCaseDocument } from "@/lib/api";
import { invalidateCase, invalidateDocuments } from "@/lib/cache/invalidate";
import UploadDropzone from "@/components/UploadDropzone";
import LibraryPickerDialog from "@/components/LibraryPickerDialog";
import DocumentCard from "@/components/DocumentCard";
import CaseQararkPicker from "@/components/CaseQararkPicker";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const UPLOAD_ROLES = ["law", "regulation", "case_detail", "reference"];
const ALL_ROLES = [...UPLOAD_ROLES, "draft"];

const ROLE_KEYS = {
  law: "case_doc_role_law",
  regulation: "case_doc_role_regulation",
  case_detail: "case_doc_role_case_detail",
  reference: "case_doc_role_reference",
  draft: "case_doc_role_draft",
};

export default function CaseDocumentsPane({
  caseId,
  documents = [],
  libraryDocuments = [],
  selectedId,
  onSelect,
}) {
  const { t } = useLang();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState("reference");

  const linkedIds = new Set(documents.map((d) => d.id));

  const refreshCase = () => invalidateCase(undefined, caseId);

  const handleUnlink = async (docId) => {
    try {
      await unlinkCaseDocument(caseId, docId);
      toast.success(t("deleted"));
      await refreshCase();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleLink = async (docId) => {
    try {
      await linkCaseDocument(caseId, docId, pickerRole);
      setPickerOpen(false);
      await refreshCase();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleUpload = async () => {
    await invalidateDocuments();
    await refreshCase();
  };

  return (
    <div className="h-full flex flex-col bg-white" data-testid="case-documents-pane">
      <div className="grid-border-b p-4 bg-slate-50/40">
        <h3 className="font-serif-display text-base text-slate-900">{t("case_documents")}</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <CaseQararkPicker
          caseId={caseId}
          active
          importMode
          onImported={async () => {
            await invalidateDocuments();
            await refreshCase();
          }}
        />
        {ALL_ROLES.map((role) => {
          const roleDocs = documents.filter((d) => d.role === role);
          const isDraftRole = role === "draft";
          return (
            <div key={role} className="grid-border-b">
              <div className="px-4 py-2 bg-slate-50 flex items-center justify-between">
                <span className="eyebrow !text-[10px]">{t(ROLE_KEYS[role])}</span>
                {!isDraftRole && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-none h-7 text-[10px]"
                    onClick={() => {
                      setPickerRole(role);
                      setPickerOpen(true);
                    }}
                  >
                    {t("case_link_library")}
                  </Button>
                )}
              </div>
              {!isDraftRole && (
                <div className="p-3 grid-border-b">
                  <UploadDropzone
                    compact
                    uploadFn={(file, onProgress) =>
                      uploadCaseDocument(caseId, file, role, onProgress)
                    }
                    onUploaded={handleUpload}
                  />
                </div>
              )}
              {isDraftRole && (
                <div className="px-4 py-2 text-[10px] text-slate-400 grid-border-b">
                  {t("case_doc_role_draft_hint")}
                </div>
              )}
              {roleDocs.length === 0 ? (
                <div className="px-4 py-3 text-xs text-slate-400">—</div>
              ) : (
                roleDocs.map((doc) => (
                  <div key={doc.id} className="relative group">
                    <DocumentCard
                      doc={doc}
                      active={selectedId === doc.id}
                      onSelect={() => onSelect?.(doc)}
                    />
                    <button
                      type="button"
                      onClick={() => handleUnlink(doc.id)}
                      className="absolute top-3 end-3 text-[10px] text-red-600 opacity-0 group-hover:opacity-100 bg-white px-2 py-1 grid-border"
                    >
                      {t("case_unlink")}
                    </button>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>

      <LibraryPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        documents={libraryDocuments.filter((d) => !linkedIds.has(d.id))}
        onSelect={handleLink}
      />
    </div>
  );
}